import React, { useState, useEffect } from 'react';
import apiService, { getTerritoryCustomers } from '../services/api';
import LoadingSpinner from '../components/ui/LoadingSpinner';

const TerritoryManagement = () => {
  const [territories, setTerritories] = useState([]);
  const [salesExecutives, setSalesExecutives] = useState([]);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showCustomersModal, setShowCustomersModal] = useState(false);
  const [selectedTerritory, setSelectedTerritory] = useState(null);
  const [editingTerritory, setEditingTerritory] = useState(null);
  const [territoryCustomers, setTerritoryCustomers] = useState([]);
  const [territoryCustomersLoading, setTerritoryCustomersLoading] = useState(false);
  const [customerFilters, setCustomerFilters] = useState({
    status: '',
    search: ''
  });
  const [filters, setFilters] = useState({
    state: '',
    district: '',
    isActive: ''
  });

  // Form state for creating/editing territories
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    country: '',
    state: '',
    district: '',
    city: ''
  });

  // Form state for assigning territories
  const [assignFormData, setAssignFormData] = useState({
    salesExecutiveId: '',
    notes: ''
  });

  useEffect(() => {
    fetchTerritories();
    fetchSalesExecutives();
    fetchStates();
  }, [filters]);

  const fetchTerritories = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.state) params.append('state', filters.state);
      if (filters.district) params.append('district', filters.district);
      if (filters.isActive !== '') params.append('isActive', filters.isActive);

      const data = await apiService.get(`/territories?${params}`);
      setTerritories(data);
    } catch (err) {
      setError('Failed to fetch territories');
    } finally {
      setLoading(false);
    }
  };

  const fetchSalesExecutives = async () => {
    try {
      const data = await apiService.get('/users?role=sales_executive');
      setSalesExecutives(data);
    } catch (err) {
      console.error('Failed to fetch sales executives:', err);
    }
  };

  const fetchStates = async () => {
    try {
      const data = await apiService.get('/territories/states');
      setStates(data);
    } catch (err) {
      console.error('Failed to fetch states:', err);
    }
  };

  const fetchDistricts = async (state) => {
    try {
      const data = await apiService.get(`/territories/districts/${encodeURIComponent(state)}`);
      setDistricts(data);
    } catch (err) {
      console.error('Failed to fetch districts:', err);
      setDistricts([]);
    }
  };

  const handleCreateTerritory = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await apiService.post('/territories', formData);
      setShowCreateModal(false);
      setFormData({
        name: '',
        description: '',
        state: '',
        district: '',
        city: ''
      });
      fetchTerritories();
    } catch (err) {
      setError('Failed to create territory');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTerritory = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await apiService.put(`/territories/${editingTerritory.id}`, formData);
      setEditingTerritory(null);
      setFormData({
        name: '',
        description: '',
        state: '',
        district: '',
        city: ''
      });
      fetchTerritories();
    } catch (err) {
      setError('Failed to update territory');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTerritory = async (id) => {
    if (!window.confirm('Are you sure you want to delete this territory?')) return;

    setLoading(true);
    try {
      await apiService.delete(`/territories/${id}`);
      fetchTerritories();
    } catch (err) {
      setError('Failed to delete territory');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignTerritory = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await apiService.post(`/territories/${selectedTerritory.id}/assign`, assignFormData);
      setShowAssignModal(false);
      setSelectedTerritory(null);
      setAssignFormData({
        salesExecutiveId: '',
        notes: ''
      });
      fetchTerritories();
    } catch (err) {
      setError('Failed to assign territory');
    } finally {
      setLoading(false);
    }
  };

  const handleUnassignTerritory = async (territoryId, executiveId) => {
    if (!window.confirm('Are you sure you want to unassign this territory?')) return;

    setLoading(true);
    try {
      await apiService.put(`/territories/${territoryId}/unassign/${executiveId}`);
      fetchTerritories();
    } catch (err) {
      setError('Failed to unassign territory');
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (territory) => {
    setEditingTerritory(territory);
    setFormData({
      name: territory.name,
      description: territory.description || '',
      country: territory.country || '',
      state: territory.state || '',
      district: territory.district || '',
      city: territory.city || ''
    });
    // Fetch districts if state is selected
    if (territory.state) {
      fetchDistricts(territory.state);
    }
  };

  const openAssignModal = (territory) => {
    setSelectedTerritory(territory);
    setAssignFormData({
      salesExecutiveId: '',
      notes: ''
    });
    setShowAssignModal(true);
  };

  const openCustomersModal = async (territory) => {
    setSelectedTerritory(territory);
    setTerritoryCustomersLoading(true);
    setShowCustomersModal(true);

    try {
      const data = await getTerritoryCustomers(territory.id, customerFilters);
      setTerritoryCustomers(data.customers);
    } catch (err) {
      console.error('Failed to fetch territory customers:', err);
      setTerritoryCustomers([]);
    } finally {
      setTerritoryCustomersLoading(false);
    }
  };

  const fetchTerritoryCustomers = async () => {
    if (!selectedTerritory) return;

    setTerritoryCustomersLoading(true);
    try {
      const data = await getTerritoryCustomers(selectedTerritory.id, customerFilters);
      setTerritoryCustomers(data.customers);
    } catch (err) {
      console.error('Failed to fetch territory customers:', err);
      setTerritoryCustomers([]);
    } finally {
      setTerritoryCustomersLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN');
  };

  const handleCountryChange = (country) => {
    setFormData({
      ...formData,
      country,
      state: '',
      district: '',
      city: ''
    });
    setDistricts([]);
  };

  const handleStateChange = (state) => {
    setFormData({
      ...formData,
      state,
      district: '',
      city: ''
    });
    if (state) {
      fetchDistricts(state);
    } else {
      setDistricts([]);
    }
  };

  return (
    <div className="w-full min-h-screen bg-gray-50 py-6 px-4 sm:px-6 lg:px-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Territory Management</h1>
        <p className="mt-2 text-gray-600">Manage territories and assign them to sales executives</p>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
            <input
              type="text"
              value={filters.state}
              onChange={(e) => setFilters({ ...filters, state: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              placeholder="Filter by state"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">District</label>
            <input
              type="text"
              value={filters.district}
              onChange={(e) => setFilters({ ...filters, district: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              placeholder="Filter by district"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={filters.isActive}
              onChange={(e) => setFilters({ ...filters, isActive: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="">All Status</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => setShowCreateModal(true)}
              className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
            >
              Create Territory
            </button>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      )}

      {/* Territories Grid */}
      {!loading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {territories.map((territory) => (
            <div key={territory.id} className="bg-white rounded-lg shadow overflow-hidden">
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{territory.name}</h3>
                    <p className="text-sm text-gray-500">{territory.description}</p>
                  </div>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${territory.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                    {territory.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>

                <div className="space-y-2 mb-4">
                  {territory.country && (
                    <div className="flex items-center text-sm text-gray-600">
                      <span className="font-medium w-16">Country:</span>
                      <span>{territory.country}</span>
                    </div>
                  )}
                  {territory.state && (
                    <div className="flex items-center text-sm text-gray-600">
                      <span className="font-medium w-16">State:</span>
                      <span>{territory.state}</span>
                    </div>
                  )}
                  {territory.district && (
                    <div className="flex items-center text-sm text-gray-600">
                      <span className="font-medium w-16">District:</span>
                      <span>{territory.district}</span>
                    </div>
                  )}
                  {territory.city && (
                    <div className="flex items-center text-sm text-gray-600">
                      <span className="font-medium w-16">City:</span>
                      <span>{territory.city}</span>
                    </div>
                  )}
                </div>

                {/* Assigned Executives */}
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Assigned Executives</h4>
                  {territory.assignedExecutives.length > 0 ? (
                    <div className="space-y-2">
                      {territory.assignedExecutives.map((assignment) => (
                        <div key={assignment.id} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {assignment.salesExecutive.name}
                            </div>
                            <div className="text-xs text-gray-500">
                              Assigned: {formatDate(assignment.assignedAt)}
                            </div>
                          </div>
                          <button
                            onClick={() => handleUnassignTerritory(territory.id, assignment.salesExecutive.id)}
                            className="text-red-600 hover:text-red-900 text-xs"
                          >
                            Unassign
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No executives assigned</p>
                  )}
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => openCustomersModal(territory)}
                    className="flex-1 bg-purple-600 text-white px-3 py-2 rounded-md text-sm hover:bg-purple-700 transition-colors"
                  >
                    View Customers
                  </button>
                  <button
                    onClick={() => openAssignModal(territory)}
                    className="flex-1 bg-green-600 text-white px-3 py-2 rounded-md text-sm hover:bg-green-700 transition-colors"
                  >
                    Assign Executive
                  </button>
                  <button
                    onClick={() => openEditModal(territory)}
                    className="px-3 py-2 text-sm text-blue-600 hover:text-blue-900 border border-blue-600 rounded-md hover:bg-blue-50 transition-colors"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDeleteTerritory(territory.id)}
                    className="px-3 py-2 text-sm text-red-600 hover:text-red-900 border border-red-600 rounded-md hover:bg-red-50 transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Territory Modal */}
      {(showCreateModal || editingTerritory) && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingTerritory ? 'Edit Territory' : 'Create New Territory'}
              </h3>
              <form onSubmit={editingTerritory ? handleUpdateTerritory : handleCreateTerritory}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Territory Name</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      rows="3"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                    <select
                      value={formData.country}
                      onChange={(e) => handleCountryChange(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    >
                      <option value="">Select Country</option>
                      <option value="India">India</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  {formData.country === 'India' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                        <select
                          value={formData.state}
                          onChange={(e) => handleStateChange(e.target.value)}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        >
                          <option value="">Select State</option>
                          {states.map((state) => (
                            <option key={state} value={state}>{state}</option>
                          ))}
                        </select>
                      </div>
                      {formData.state && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">District</label>
                          <select
                            value={formData.district}
                            onChange={(e) => setFormData({ ...formData, district: e.target.value })}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                          >
                            <option value="">Select District</option>
                            {districts.map((district) => (
                              <option key={district} value={district}>{district}</option>
                            ))}
                          </select>
                        </div>
                      )}
                    </>
                  )}
                  {formData.country !== 'India' && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">State/Province</label>
                        <input
                          type="text"
                          value={formData.state}
                          onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">District/County</label>
                        <input
                          type="text"
                          value={formData.district}
                          onChange={(e) => setFormData({ ...formData, district: e.target.value })}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        />
                      </div>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                    <input
                      type="text"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                </div>
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateModal(false);
                      setEditingTerritory(null);
                      setFormData({
                        name: '',
                        description: '',
                        country: '',
                        state: '',
                        district: '',
                        city: ''
                      });
                      setDistricts([]);
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                    disabled={loading}
                  >
                    {loading ? 'Saving...' : (editingTerritory ? 'Update' : 'Create')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Assign Territory Modal */}
      {showAssignModal && selectedTerritory && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Assign Territory: {selectedTerritory.name}
              </h3>
              <form onSubmit={handleAssignTerritory}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Sales Executive</label>
                    <select
                      value={assignFormData.salesExecutiveId}
                      onChange={(e) => setAssignFormData({ ...assignFormData, salesExecutiveId: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      required
                    >
                      <option value="">Select Executive</option>
                      {salesExecutives.map(exec => (
                        <option key={exec.id} value={exec.id}>{exec.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                    <textarea
                      value={assignFormData.notes}
                      onChange={(e) => setAssignFormData({ ...assignFormData, notes: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      rows="3"
                    />
                  </div>
                </div>
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAssignModal(false);
                      setSelectedTerritory(null);
                      setAssignFormData({
                        salesExecutiveId: '',
                        notes: ''
                      });
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700"
                    disabled={loading}
                  >
                    {loading ? 'Assigning...' : 'Assign'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Territory Customers Modal */}
      {showCustomersModal && selectedTerritory && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-6xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Customers in Territory: {selectedTerritory.name}
                </h3>
                <button
                  onClick={() => {
                    setShowCustomersModal(false);
                    setSelectedTerritory(null);
                    setTerritoryCustomers([]);
                    setCustomerFilters({ status: '', search: '' });
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Territory Info */}
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-700">State:</span>
                    <span className="ml-2 text-gray-900">{selectedTerritory.state}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">District:</span>
                    <span className="ml-2 text-gray-900">{selectedTerritory.district || 'All Districts'}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">City:</span>
                    <span className="ml-2 text-gray-900">{selectedTerritory.city || 'All Cities'}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Status:</span>
                    <span className={`ml-2 px-2 py-1 text-xs font-medium rounded-full ${selectedTerritory.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                      {selectedTerritory.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Customer Filters */}
              <div className="bg-white p-4 rounded-lg border mb-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Search Customers</label>
                    <input
                      type="text"
                      value={customerFilters.search}
                      onChange={(e) => setCustomerFilters({ ...customerFilters, search: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      placeholder="Search by name, business, email..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                      value={customerFilters.status}
                      onChange={(e) => setCustomerFilters({ ...customerFilters, status: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    >
                      <option value="">All Status</option>
                      <option value="approved">Approved</option>
                      <option value="pending">Pending</option>
                      <option value="rejected">Rejected</option>
                    </select>
                  </div>
                  <div className="flex items-end">
                    <button
                      onClick={fetchTerritoryCustomers}
                      className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
                    >
                      Apply Filters
                    </button>
                  </div>
                </div>
              </div>

              {/* Customers List */}
              {territoryCustomersLoading ? (
                <div className="flex justify-center items-center h-64">
                  <LoadingSpinner size="lg" />
                </div>
              ) : (
                <div className="bg-white rounded-lg border overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Customer
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Business
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Contact
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Assigned Executive
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Created
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {territoryCustomers.length > 0 ? (
                          territoryCustomers.map((customer) => (
                            <tr key={customer.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div>
                                  <div className="text-sm font-medium text-gray-900">{customer.name}</div>
                                  <div className="text-sm text-gray-500">{customer.email}</div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">{customer.businessName || '-'}</div>
                                <div className="text-sm text-gray-500">{customer.businessCategory || '-'}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">{customer.contactName || '-'}</div>
                                <div className="text-sm text-gray-500">{customer.contactMobile || '-'}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`px-2 py-1 text-xs font-medium rounded-full ${customer.status === 'approved' ? 'bg-green-100 text-green-800' :
                                    customer.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                      'bg-red-100 text-red-800'
                                  }`}>
                                  {customer.status}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">
                                  {customer.assignedSalesExecutive ? customer.assignedSalesExecutive.name : '-'}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {customer.assignedSalesExecutive ? customer.assignedSalesExecutive.employeeId : '-'}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {formatDate(customer.createdAt)}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="6" className="px-6 py-4 text-center text-sm text-gray-500">
                              No customers found in this territory
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TerritoryManagement;
